/* ============================================================
   Subscription enforcement for households.

   Deliberately NOT SubscriptionGate. That gate's locked state renders
   SubscriptionLockout, whose primary button is a mock that writes a 30-day
   expiry straight into Firestore from the browser — fine while nobody paid,
   unacceptable on a consumer SKU. A locked household gets the real billing
   page instead, with the real Paystack handoff.

   The grace state also reads differently here: a household's pilot took no
   card, so "expiring" is an invitation, not a failed charge.
   ============================================================ */
import React from 'react';
import { Link } from 'react-router-dom';
import { OrgSubscription } from '../../services/useOrgSubscription';
import FamilyBillingPage from './FamilyBillingPage';
import { FAM, FAMILY_PRICE_LABEL, UI_FONT } from './familyTheme';

interface Props {
  sub: OrgSubscription;
  orgCode: string;
  orgName: string;
  children: React.ReactNode;
}

/** Shown above the console while the clock is running down. */
export function FamilyGraceBanner({ sub }: { sub: OrgSubscription }) {
  if (sub.status !== 'grace') return null;
  const days = sub.daysLeft;
  const msg = sub.isPilot
    ? days >= 0
      ? `Your free days end in ${days} day${days === 1 ? '' : 's'}.`
      : 'Your free days have ended.'
    : days >= 0
      ? `Your subscription renews in ${days} day${days === 1 ? '' : 's'}.`
      : `Your subscription expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago.`;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      background: FAM.tint, borderBottom: `1px solid ${FAM.tint2}`,
      padding: '10px clamp(14px,4vw,24px)', fontFamily: UI_FONT, fontSize: 13.5, color: FAM.ink,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: FAM.accent, flex: '0 0 7px' }} />
      <span>
        <b>{msg}</b>{' '}
        {sub.isPilot
          ? `We have no card on file, so nothing is charged automatically — ${FAMILY_PRICE_LABEL} keeps everything on.`
          : 'Keep your household running with one payment.'}
      </span>
      <Link to="/families/billing" style={{ marginLeft: 'auto', whiteSpace: 'nowrap', fontWeight: 800, color: FAM.accent, textDecoration: 'none' }}>
        Go to billing →
      </Link>
    </div>
  );
}

export default function FamilySubscriptionGate({ sub, orgCode, orgName, children }: Props) {
  if (sub.status === 'loading') return null;
  if (sub.status === 'locked') {
    return <FamilyBillingPage orgCode={orgCode} orgName={orgName} sub={sub} standalone />;
  }
  return (
    <>
      <FamilyGraceBanner sub={sub} />
      {children}
    </>
  );
}
